import React from 'react';

type ProgressEvent = {
  type?: string;
  status?: string;
  stage?: string;
  tool?: string;
  filePath?: string;
};

const TOOL_LABELS: Record<string, string> = {
  write_file: 'Writing file',
  edit_file: 'Editing file',
  read_file: 'Reading file',
  deploy: 'Deploying',
  list_files: 'Exploring files',
  search_files: 'Searching',
};

function getToolLabel(tool?: string): string {
  if (!tool) return 'Running command';
  return TOOL_LABELS[tool] || 'Running command';
}

function getLastSnippet(text: string): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= 150) return trimmed;
  const tail = trimmed.slice(-150);
  const lineBreak = tail.indexOf('\n');
  if (lineBreak >= 0 && lineBreak < 80) return tail.slice(lineBreak + 1).trim();
  const sentenceStart = tail.indexOf('. ');
  if (sentenceStart >= 0 && sentenceStart < 80) return tail.slice(sentenceStart + 2).trim();
  return tail.trim();
}

export default function AgentProgressCard({
  lastEvent,
  textPreview,
}: {
  lastEvent?: ProgressEvent | null;
  textPreview?: string;
}) {
  const toolLabel = getToolLabel(lastEvent?.tool);
  const description = (lastEvent?.stage || '').trim() || 'Working...';
  const snippet = getLastSnippet(textPreview || '');

  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    setElapsed(0);
    const timer = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [lastEvent?.stage]);

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
      <div className="flex items-center">
        <span className="text-[14px] text-white/80 font-semibold">{toolLabel}</span>
        {elapsed >= 2 && (
          <span className="text-[11px] text-white/20 ml-2">{elapsed}s</span>
        )}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className="ml-auto h-4 w-4 text-white/20 shrink-0"
        >
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="text-[13px] text-white/40 mt-1 truncate">
        {description}
      </div>
      {snippet && (
        <div className="mt-2 pt-2 border-t border-white/[0.04] text-[13px] text-white/30 line-clamp-2">
          {snippet}
        </div>
      )}
    </div>
  );
}
